import { useCallback, useMemo, useState } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { useGetAttachmentBuckets } from "api/queries/common/useGetAttachmentBuckets";
import { useGetPackingListConfig } from "api/queries/location/useGetPackingListConfig";
import { useGetServiceDetailsConfig } from "api/queries/location/useGetServiceDetailsConfig";
import { useGetQuoteRequestTimerSettings } from "api/queries/quotes/useGetQuoteRequestTimerSettings";
import usePermissions from "hooks/usePermissions";
import useFeatureFlag from "hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "constants/featureFlags";
import { ScheduleStopValue } from "pages/shipper/RequestFormV2/defaults/scheduleDefaults";
import { buildHandlingUnitHashMap } from "@RequestFormV2/sections/PackingList/utils/buildHandlingUnitHashMap";
import { useIsParcelFreight } from "@RequestFormV2/sections/PackingList/hooks/useIsParcelFreight";
import useShowConsigneeSection from "@RequestFormV2/sections/Schedule/hooks/useShowConsigneeSection";
import { useOrganisationData } from "@RequestFormV2/sections/Organisation/context/useOrganisationData";
import { buildErroredSections } from "@RequestFormV2/utils/buildErroredSections";
import { useEditShipmentLock } from "@RequestFormV2/context/editShipmentLock";
import { useRequestFormMode } from "@RequestFormV2/context/requestFormMode";
import { validateOnReview } from "@RequestFormV2/validation/validateOnReview";
import { getHasMultipleCountries } from "@RequestFormV2/validation/getHasMultipleCountries";
import { flattenReviewErrors, trackQuoteFormErrorLogged } from "@RequestFormV2/formEvents/requestFormAnalytics";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useReviewValidation(methods: UseFormReturn<any>) {
  const intl = useIntl();
  const mode = useRequestFormMode();
  const isQuickRequest = mode === "quick";
  const isEditShipment = useEditShipmentLock();
  const [erroredSections, setErroredSections] = useState<string[]>([]);
  const [hasAttemptedReview, setHasAttemptedReview] = useState(false);

  const selectedLocation = useWatch({ control: methods.control, name: "organizational.location" });
  const stops = useWatch({ control: methods.control, name: "schedule.stops" }) as ScheduleStopValue[] | undefined;

  const { organisationFields, invoiceLocationDetails, shipmentTitleDetails } = useOrganisationData();
  const { data: serviceDetailsConfig } = useGetServiceDetailsConfig(selectedLocation ?? "", {
    enabled: !!selectedLocation,
  });
  const { data: packingListConfig } = useGetPackingListConfig(selectedLocation ?? "", {
    enabled: !!selectedLocation,
  });
  const { parsedPermissions } = usePermissions();
  const hasAttachmentBuckets = parsedPermissions?.company_shipper_settings?.request_attachment_buckets ?? false;
  const { data: bucketAttachments = [] } = useGetAttachmentBuckets(selectedLocation ?? "", isQuickRequest, {
    enabled: !!selectedLocation && hasAttachmentBuckets,
    refetchOnMount: false,
  });

  const { data: timingSettings } = useGetQuoteRequestTimerSettings({
    refetchOnMount: false,
  });

  const showConsigneeSection = useShowConsigneeSection();
  const isParcelFreight = useIsParcelFreight();
  const isOptionalPortAddressFormEnabled = useFeatureFlag(FEATURE_FLAGS.FF_WEB10382_OPTIONAL_PORT_ADDRESS_FORM);

  const hasMultipleCountries = useMemo(() => getHasMultipleCountries(stops), [stops]);

  const handlingUnitHashMap = useMemo(
    () => buildHandlingUnitHashMap(packingListConfig?.handling_unit_options),
    [packingListConfig?.handling_unit_options],
  );

  const syncErroredSections = useCallback(() => {
    setErroredSections(buildErroredSections(methods.formState.errors, mode));
  }, [methods.formState.errors, mode]);

  const clearFieldError = useCallback(
    (path: string) => {
      methods.clearErrors(path);
      // formState.errors can lag one tick behind clearErrors — defer section sync.
      queueMicrotask(() => {
        queueMicrotask(() => {
          setErroredSections(buildErroredSections(methods.formState.errors, mode));
        });
      });
    },
    [methods],
  );

  const runReviewValidation = useCallback(async () => {
    setHasAttemptedReview(true);

    const result = validateOnReview(methods, intl, {
      organisationFields,
      invoiceLocationDetails,
      shipmentTitleDetails,
      selectedLocation,
      serviceDetailsConfig,
      showConsigneeSection,
      hasMultipleCountries,
      handlingUnitHashMap,
      nmfcNumberEnabled: packingListConfig?.nmfc_number_enabled,
      isParcelFreight,
      bucketAttachments,
      hasAttachmentBuckets,
      isOptionalPortAddressFormEnabled,
      isEditShipment,
      mode,
      quotingDeadlineMinimumMinutes: timingSettings?.quoting_deadline_minimum_time_minutes,
      preferredTimezone: parsedPermissions?.preferred_timezone,
    });

    setErroredSections(result.erroredSections);

    if (!result.isValid) {
      trackQuoteFormErrorLogged(flattenReviewErrors(result.fieldErrors));
    }

    return result;
  }, [
    methods,
    intl,
    organisationFields,
    invoiceLocationDetails,
    shipmentTitleDetails,
    selectedLocation,
    serviceDetailsConfig,
    showConsigneeSection,
    hasMultipleCountries,
    handlingUnitHashMap,
    packingListConfig?.nmfc_number_enabled,
    isParcelFreight,
    bucketAttachments,
    hasAttachmentBuckets,
    isOptionalPortAddressFormEnabled,
    isEditShipment,
    mode,
    timingSettings?.quoting_deadline_minimum_time_minutes,
    parsedPermissions?.preferred_timezone,
  ]);

  return {
    erroredSections,
    hasAttemptedReview,
    runReviewValidation,
    clearFieldError,
    syncErroredSections,
  };
}
